import dayjs from "dayjs";
import { getMarketStatus } from "../../components/StockDetails/utils/getMarketStatus";
import { TradingPhase } from "../../components/StockDetails/utils/tradingPhase";
import { I18nUtil } from "@utils/i18n";
import type { IUseStockPriceProps } from "../useStockPrice";
import Translations from "@translate/hooks/useTokenPopoverData/getStockPriceData";

const timeFormat = "MMM DD, HH:mm:ss";
const I18n = I18nUtil.createScoped(Translations);

export const getStockPriceData = ({
  marketData,
  currentPrice,
}: IUseStockPriceProps) => {
  const freshCurrentPrice = Number(currentPrice);
  const now = dayjs().valueOf();

  const {
    pre_market_open_time,
    post_market_close_time,
    previous_regular_market_close_time,
    regular_market_open_time,
    regular_market_close_time,
    next_pre_market_open_time,
    close,
    previous_close,
    previous_post_market_close_time,
  } = marketData || {};

  const marketStatus = getMarketStatus(now, {
    pre_market_open_time: pre_market_open_time!,
    regular_market_open_time: regular_market_open_time!,
    regular_market_close_time: regular_market_close_time!,
    post_market_close_time: post_market_close_time!,
    next_pre_market_open_time: next_pre_market_open_time!,
  });

  const shouldUsePreviousClose = (): boolean => {
    return (
      (post_market_close_time && now <= post_market_close_time) ||
      (!post_market_close_time &&
        !pre_market_open_time &&
        !regular_market_open_time)
    );
  };

  let marketDataList: Array<{
    current_price: number;
    price_change: number;
    price_change_percentage: string | number;
    market_status: TradingPhase;
    marketLabel: string;
    offsetInHours: number;
  }> = [];

  if (marketStatus === TradingPhase.PRE_MARKET) {
    const previousRegularMarketCloseTime = dayjs(
      previous_regular_market_close_time,
    ).tz("America/New_York");
    const time = previousRegularMarketCloseTime.format(timeFormat);
    const offsetInHours = previousRegularMarketCloseTime.utcOffset() / 60;
    const previousChange = close! - previous_close!;
    const previousChangePercentage =
      (previousChange / (previous_close || 1)) * 100;
    const freshPriceChange = freshCurrentPrice - close!;
    marketDataList = [
      {
        current_price: close!,
        price_change: previousChange,
        price_change_percentage: `${previousChangePercentage || 0}`,
        market_status: TradingPhase.PRE_MARKET,
        marketLabel: I18n.t("At close: %{var0}", {
          var0: previous_regular_market_close_time ? time : "--",
        }),
        offsetInHours: offsetInHours,
      },
      {
        current_price: freshCurrentPrice,
        price_change: freshPriceChange ? freshPriceChange : 0,
        price_change_percentage: freshPriceChange
          ? (freshPriceChange / (close || 1)) * 100
          : 0,
        market_status: TradingPhase.PRE_MARKET,
        marketLabel: I18n.t("Pre-market: %{var0}", {
          var0: dayjs().tz("America/New_York").format(timeFormat),
        }),
        offsetInHours: offsetInHours,
      },
    ];
  } else if (marketStatus === TradingPhase.IN_MARKET) {
    const currentMarketTime = dayjs().tz("America/New_York");
    const offsetInHours = currentMarketTime.utcOffset() / 60;
    const freshPriceChange = freshCurrentPrice - previous_close!;
    marketDataList = [
      {
        current_price: freshCurrentPrice,
        price_change: freshPriceChange ? freshPriceChange : 0,
        price_change_percentage: freshPriceChange
          ? (freshPriceChange / (previous_close || 1)) * 100
          : 0,
        market_status: TradingPhase.IN_MARKET,
        marketLabel: I18n.t("As of today:%{var0}", {
          var0: currentMarketTime.format(timeFormat),
        }),
        offsetInHours: offsetInHours,
      },
    ];
  } else if (
    marketStatus === TradingPhase.AFTER_MARKET ||
    marketStatus === TradingPhase.CLOSED_MARKET
  ) {
    const shouldUsePreviousData = shouldUsePreviousClose();

    const firstMarketTime =
      marketStatus === TradingPhase.CLOSED_MARKET && shouldUsePreviousData
        ? previous_regular_market_close_time
        : regular_market_close_time;

    const regularMarketCloseTime =
      dayjs(firstMarketTime).tz("America/New_York");
    const regularMarketCloseTimeFormat =
      regularMarketCloseTime.format(timeFormat);
    const offsetInHours = regularMarketCloseTime.utcOffset() / 60;
    const closePriceChange = close! - previous_close!;
    const freshPriceChange = freshCurrentPrice - close!;

    const lastMarketTime =
      marketStatus === TradingPhase.CLOSED_MARKET
        ? dayjs(
            shouldUsePreviousData
              ? previous_post_market_close_time
              : post_market_close_time,
          )
        : dayjs();
    marketDataList = [
      {
        current_price: close!,
        price_change: closePriceChange ? closePriceChange : 0,
        price_change_percentage: closePriceChange
          ? (closePriceChange / (previous_close || 1)) * 100
          : 0,
        market_status: TradingPhase.AFTER_MARKET,
        marketLabel: I18n.t("At close: %{var0}", {
          var0: firstMarketTime ? regularMarketCloseTimeFormat : "--",
        }),
        offsetInHours: offsetInHours,
      },
      {
        current_price: freshCurrentPrice,
        price_change: freshPriceChange ? freshPriceChange : 0,
        price_change_percentage: freshPriceChange
          ? (freshPriceChange / (close || 1)) * 100
          : 0,
        market_status: TradingPhase.AFTER_MARKET,
        marketLabel: I18n.t("Post-market: %{var0}", {
          var0: lastMarketTime.tz("America/New_York").format(timeFormat),
        }),
        offsetInHours: offsetInHours,
      },
    ];
  }

  return {
    marketDataList,
  };
};
